import {
  AlertIcon,
  BookIcon,
  ClockIcon,
  CodeIcon,
  DatabaseIcon,
  GearIcon,
  GraphIcon,
  HistoryIcon,
  FileCodeIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  GoalIcon,
  LogIcon,
  PeopleIcon,
  PulseIcon,
  RepoPushIcon,
  ShieldIcon,
  VerifiedIcon,
  WorkflowIcon,
} from "@primer/octicons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, Outlet, useLocation, useParams, useSearchParams } from "react-router-dom";
import { HttpError, rememberOwner, rp } from "../api/client";
import { useRepo } from "../api/hooks";
import { openCount } from "../api/types";
import { Empty, ErrorMessage } from "./common";
import { DocTree } from "./DocTree";
import { cn } from "../lib/utils";
import { SideNav, sideNavItem } from "./SettingsLayout";
import { type NavTab, SubNav } from "./SubNav";
import { Counter } from "./ui/badge";

type Entry = { to: string; Icon: typeof CodeIcon; label: string; on: (rest: string, view: string) => boolean; count?: number; items?: Entry[] };

const under = (rest: string, ...ps: string[]) => ps.some((p) => rest === p || rest.startsWith(`${p}/`));

/** The repository tabs, continuing the app header band, above the routed repo page; the current tab's sub-pages are a side menu beside it. */
export function RepoLayout() {
  const { owner, repo = "" } = useParams();
  rememberOwner(repo, owner);
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const q = useRepo(repo);
  // Queries are keyed by the repo's name, which two owners can share: refetch the cached ones when the owner changes.
  const qc = useQueryClient();
  const seen = useRef(owner);
  useEffect(() => {
    if (seen.current !== owner) qc.invalidateQueries({ predicate: (x) => x.queryKey.includes(repo) });
    seen.current = owner;
  }, [owner, repo, qc]);
  const info = q.data?.info;
  const open = info ? openCount(info.counts) : 0;
  const frozen = info ? (info.frozen ? 1 : 0) + info.frozen_paths.length : 0;
  const rest = `/${pathname.split("/").slice(3).join("/")}`.replace(/^\/$/, "");
  // The CI page picks its view from the query string; a job filter implies the runs view.
  const view = params.get("view") ?? (params.get("job") ? "runs" : "");
  // Ordered for humans supervising agents: health, what people asked for, the changes (what needs them first), docs, then code and settings.
  const entries: Entry[] = [
    { to: "/overview", Icon: PulseIcon, label: "Overview", on: (r) => under(r, "/overview") },
    { to: "/intents", Icon: GoalIcon, label: "Intents", on: (r) => under(r, "/intents") },
    {
      to: "/ci",
      Icon: GitPullRequestIcon,
      label: "Changes",
      count: open,
      on: (r) => under(r, "/ci", "/changes", "/remote"),
      items: [
        { to: "/ci", Icon: GitPullRequestIcon, label: "Changes", count: open, on: (r, v) => (r === "/ci" && !v) || under(r, "/changes") },
        { to: "/ci?view=runs", Icon: WorkflowIcon, label: "Runs", on: (r, v) => (r === "/ci" && v === "runs") || under(r, "/ci/runs", "/ci/log") },
        { to: "/ci?view=queue", Icon: ClockIcon, label: "Queue", on: (r, v) => r === "/ci" && v === "queue" },
        { to: "/ci?view=verify", Icon: VerifiedIcon, label: "Verification", on: (r, v) => r === "/ci" && v === "verify" },
        { to: "/remote", Icon: GitMergeIcon, label: "Remote PRs", on: (r) => under(r, "/remote") },
      ],
    },
    {
      to: "/docs",
      Icon: BookIcon,
      label: "Docs",
      on: (r) => under(r, "/docs", "/knowledge"),
      // The docs page picks its view from the query string, as the CI page does.
      // One view, so no menu: the side panel holds the file tree. Attention leads the list; the board
      // is the Intents tab, the graph is Code's.
      items: [],
    },
    {
      to: "",
      Icon: CodeIcon,
      label: "Code",
      on: (r) => r === "" || under(r, "/src", "/commits", "/commit", "/graph", "/activity"),
      items: [
        { to: "", Icon: FileCodeIcon, label: "Files", on: (r) => r === "" || under(r, "/src") },
        { to: "/commits", Icon: HistoryIcon, label: "History", on: (r) => under(r, "/commits", "/commit") },
        { to: "/graph", Icon: GraphIcon, label: "Graph", on: (r) => under(r, "/graph") },
        { to: "/activity", Icon: LogIcon, label: "Activity", on: (r) => under(r, "/activity") },
      ],
    },
    {
      to: "/settings",
      Icon: GearIcon,
      label: "Settings",
      on: (r) => under(r, "/settings"),
      items: [
        { to: "/settings", Icon: GearIcon, label: "General", on: (r) => r === "/settings" },
        { to: "/settings/policy", Icon: ShieldIcon, label: "Policy", on: (r) => under(r, "/settings/policy") },
        { to: "/settings/access", Icon: PeopleIcon, label: "Access", on: (r) => under(r, "/settings/access") },
        { to: "/settings/remote", Icon: RepoPushIcon, label: "Remote", on: (r) => under(r, "/settings/remote") },
        { to: "/settings/storage", Icon: DatabaseIcon, label: "Storage", on: (r) => under(r, "/settings/storage") },
        { to: "/settings/danger", Icon: AlertIcon, label: "Danger zone", count: frozen, on: (r) => under(r, "/settings/danger") },
      ],
    },
  ];
  const resolve = ({ to, on, items, ...e }: Entry): NavTab => ({ ...e, to: `${rp(repo)}${to}`, current: on(rest, view), items: items?.map(resolve) });
  const tabs = entries.map(resolve);
  const tab = tabs.find((t) => t.current);
  // A single change's or commit's diff wants the whole window, so it drops the side menu; every other page keeps it.
  const diff = /^\/(changes\/[^/]+|commit\/.*)$/.test(rest);
  const sub = diff ? undefined : tab?.items;
  // Documents are read, so they get a wider column than the other pages.
  const maxW = /^\/docs(\/|$)/.test(rest) ? "max-w-[1680px]" : "max-w-[1280px]";
  // Diffs use the whole width. Elsewhere the side menu is pinned to the left edge and the content is centered in the rest.
  const content = (
    <div className={cn("min-w-0", !diff && `mx-auto mt-6 w-full ${maxW}`, !diff && sub && "px-6 pb-12 max-md:px-4")}>
      <Outlet />
    </div>
  );
  return (
    <>
      <SubNav label="Repository" tabs={tabs} />
      <div className={cn("flex flex-1 flex-col", !sub && "px-6 pb-12 max-md:px-4")}>
        {q.error instanceof HttpError && q.error.status === 404 ? (
          <Empty bee="failed">
            <b className="text-foreground">{repo}</b> does not exist, or you do not have access to it. List the repositories you can see with{" "}
            <code>bee ls</code>.
          </Empty>
        ) : q.error ? (
          <ErrorMessage error={q.error} />
        ) : sub ? (
          <div className="grid flex-1 grid-cols-[260px_minmax(0,1fr)] items-stretch max-md:grid-cols-[minmax(0,1fr)] max-md:grid-rows-[auto_1fr]">
            <aside className="border-r border-border-muted bg-card/40 max-md:border-b max-md:border-r-0">
              <div className="sticky top-4 mt-6 flex max-h-[calc(100vh-2rem)] min-h-0 flex-col gap-4 px-4 max-md:static max-md:max-h-none max-md:mt-4 max-md:pb-2">
                {sub.length > 1 && (
                <SideNav label={tab?.label ?? "Repository"}>
                  {sub.map((i) => (
                    <Link key={i.label} to={i.to} className={sideNavItem} aria-current={i.current ? "page" : undefined}>
                      <i.Icon size={16} /> <span className="min-w-0 flex-1">{i.label}</span> {i.count ? <Counter>{i.count}</Counter> : null}
                    </Link>
                  ))}
                </SideNav>
                )}
                {tab?.label === "Docs" && <DocTree repo={repo} />}
              </div>
            </aside>
            {content}
          </div>
        ) : (
          content
        )}
      </div>
    </>
  );
}
